import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Tooltip,
  Icon,
  IconButton,
  withStyles,
  TextField,
} from '@material-ui/core';
import { debounce } from 'lodash';

import { editTagType } from '@pingum/app-state-management/tagType/actions';
import ColorPicker from '../../../../widgets/ColorPicker';
import IconPicker from '../../../../widgets/IconPicker';
import styles from './styles';

export const DEBOUNCE_TIME = 1000;

const isNameValid = (name) => (
  name !== null
  && name !== undefined
  && name.length >= 2
  && name.length <= 50
);

const isDescriptionValid = (description) => (
  !!description
    && description.length <= 150
);

export class TagTypeEditorComponent extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      updateTagTypeName: props.tagType.name,
      updateTagTypeDescription: props.tagType.description,
      validation: {
        name: {
          isValid: true,
          message: '',
        },
        description: {
          isValid: true,
          message: '',
        },
      },
    };
  }

  componentDidMount() {
    this.debouncedSaveName = debounce(this.saveName, DEBOUNCE_TIME);
    this.debouncedSaveDescription = debounce(this.saveDescription, DEBOUNCE_TIME);
  }

  patchTagType = ({
    id = this.props.tagType.id,
    ...request
  }) => {
    this.props.editTagType(id, request);
  }

  saveName = () => {
    const isValid = isNameValid(this.state.updateTagTypeName);
    if (isValid) {
      this.patchTagType({ name: this.state.updateTagTypeName });
    }
    this.setState((prevState) => ({
      validation: {
        ...prevState.validation,
        name: {
          isValid,
          message: !isValid
            ? <span>Please provide a valid display name (between 2 and 50 characters in length).</span>
            : '',
        },
      },
    }));
  }

  saveDescription = () => {
    const isValid = isDescriptionValid(this.state.updateTagTypeDescription);
    if (isValid) {
      this.patchTagType({ description: this.state.updateTagTypeDescription });
    }
    this.setState((prevState) => ({
      validation: {
        ...prevState.validation,
        description: {
          isValid,
          message: !isValid
            ? <span>Please provide a valid description (up to 150 characters in length).</span>
            : '',
        },
      },
    }));
  }

  handleArchive = () => {
    this.props.editTagType(this.props.tagType.id, { archived: true });
  }

  handleUnarchive = () => {
    this.patchTagType({ archived: false });
  }

  handleNameChange = (e) => {
    this.setState({ updateTagTypeName: e.target.value }, () => {
      this.debouncedSaveName();
    });
  }

  handleDescriptionChange = (e) => {
    this.setState({ updateTagTypeDescription: e.target.value }, () => {
      this.debouncedSaveDescription();
    });
  }

  handleColorChange = (event) => {
    this.patchTagType({ iconColor: event.hex });
  }

  handleIconChange = (icon) => {
    this.patchTagType({ icon });
  }

  render() {
    const {
      tagType,
      classes,
    } = this.props;
    return (
      <div className={classes.card} style={{ display: 'flex' }}>
        <div className={classes.cardHeader}>
          <div style={{ flex: 1 }}>
            <h4>Tag Category</h4>
          </div>
          <div>
            {
              !tagType.archived
              && (
                <Tooltip title="Archive">
                  <IconButton onClick={this.handleArchive}>
                    <Icon>delete</Icon>
                  </IconButton>
                </Tooltip>
              )
            }
            {
              tagType.archived
              && (
                <Tooltip title="Restore">
                  <IconButton onClick={this.handleUnarchive}>
                    <Icon>restore_from_trash</Icon>
                  </IconButton>
                </Tooltip>
              )
            }
          </div>
        </div>
        <div>
          <TextField
            id={`status-name-${tagType.id}`}
            value={this.state.updateTagTypeName}
            onChange={this.handleNameChange}
            variant="outlined"
            label="Category Name"
            error={!this.state.validation.name.isValid}
            helperText={
              !this.state.validation.name.isValid
              && this.state.validation.name.message
            }
            fullWidth
            margin="normal"
          />
          <TextField
            id={'status-description'.concat(tagType.id)}
            value={this.state.updateTagTypeDescription}
            onChange={this.handleDescriptionChange}
            multiline
            rows="2"
            variant="outlined"
            label="Description"
            error={!this.state.validation.description.isValid}
            helperText={
              !this.state.validation.description.isValid
              && this.state.validation.description.message
            }
            fullWidth
            margin="normal"
          />
          <ColorPicker color={tagType.iconColor} onColorChange={this.handleColorChange} />
          <IconPicker icon={tagType.icon} onIconChange={this.handleIconChange} />
        </div>
      </div>
    );
  }
}

TagTypeEditorComponent.propTypes = {
  tagType: PropTypes.object.isRequired,
  editTagType: PropTypes.func.isRequired,
  classes: PropTypes.object,
};

TagTypeEditorComponent.defaultProps = { classes: {} };

export const mapDispatchToProps
  = (dispatch) => ({ editTagType: (tagTypeId, request) => dispatch(editTagType(tagTypeId, request)) });

export default withStyles(styles)(connect(
  null,
  mapDispatchToProps,
)(TagTypeEditorComponent));
